import React, { useRef } from 'react';
import { IconUpload, IconX, IconSignature } from '@tabler/icons-react';

// Optional signature: none, typed name, or uploaded image (forces multipart).
export const EMPTY_SIGNATURE = { mode: 'none', name: '', image: null };

const MODES = [
    { value: 'none', label: 'No signature' },
    { value: 'typed', label: 'Type my name' },
    { value: 'upload', label: 'Upload image' },
];

export default function SignatureField({ value = EMPTY_SIGNATURE, onChange, defaultName = '', error }) {
    const fileInputRef = useRef(null);

    const setMode = (mode) => {
        if (mode === 'typed') onChange({ mode, name: value.name || defaultName, image: null });
        else if (mode === 'upload') onChange({ mode, name: '', image: value.image || null });
        else onChange({ ...EMPTY_SIGNATURE });
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        onChange({ mode: 'upload', name: '', image: file });
    };

    const clearFile = () => {
        onChange({ mode: 'upload', name: '', image: null });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="border border-border rounded-[4px] p-15">
            <div className="flex items-center gap-8 mb-10">
                <IconSignature size={16} className="text-text-secondary" />
                <span className="text-sm font-medium">Signature</span>
                <span className="text-xs text-text-secondary">(optional)</span>
            </div>

            <div className="flex flex-wrap gap-15 mb-10">
                {MODES.map(mode => (
                    <label key={mode.value} className="flex items-center gap-5 text-sm cursor-pointer">
                        <input
                            type="radio"
                            name="signature-mode"
                            checked={value.mode === mode.value}
                            onChange={() => setMode(mode.value)}
                        />
                        {mode.label}
                    </label>
                ))}
            </div>

            {value.mode === 'typed' && (
                <input
                    type="text"
                    value={value.name}
                    onChange={(e) => onChange({ ...value, name: e.target.value })}
                    placeholder="Name to print on the signature line"
                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                />
            )}

            {value.mode === 'upload' && (
                <div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    {value.image ? (
                        <div className="flex items-center justify-between gap-10 bg-bg-light border border-border rounded-[4px] px-10 py-[8px]">
                            <span className="text-sm truncate">{value.image.name}</span>
                            <button type="button" onClick={clearFile} className="text-text-secondary hover:text-danger">
                                <IconX size={14} />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="btn btn-outline-secondary text-xs px-10 py-5 flex items-center gap-5"
                        >
                            <IconUpload size={14} />
                            Choose signature image
                        </button>
                    )}
                </div>
            )}

            {error && <p className="text-danger text-xs mt-5">{error}</p>}
        </div>
    );
}
